/**
 * Position homes inside an incident. Keyed by the roster position name.
 * Unit homes stay in the list whether or not someone holds the seat.
 * Command Staff and the four section chiefs appear once the incident is
 * escalated.
 */

export type SeatSection =
  | 'command'
  | 'planning'
  | 'operations'
  | 'logistics'
  | 'finance'
  | 'intelligence';

export type SeatActivation = 'command-staff' | 'section-chief' | 'staffed';

export type SeatPanelId =
  | 'command'
  | 'safety'
  | 'liaison'
  | 'pio'
  | 'legal'
  | 'planning'
  | 'situation'
  | 'resources'
  | 'documentation'
  | 'demobilization'
  | 'operations'
  | 'logistics'
  | 'communications'
  | 'medical'
  | 'facilities'
  | 'supply'
  | 'ground-support'
  | 'sustainment'
  | 'finance'
  | 'time'
  | 'procurement'
  | 'cost'
  | 'claims'
  | 'intel';

export type RelatedForm = {
  baseId: string;
  label: string;
};

export type SeatDefinition = {
  positionName: string;
  /** Roster names that open this home. Defaults to `positionName`. */
  rosterNames?: readonly string[];
  slug: string;
  label: string;
  section: SeatSection;
  activation: SeatActivation;
  panel: SeatPanelId;
  relatedForms: RelatedForm[];
};

export const SEAT_SECTIONS: { id: SeatSection; label: string }[] = [
  { id: 'command', label: 'Command' },
  { id: 'planning', label: 'Planning' },
  { id: 'operations', label: 'Operations' },
  { id: 'logistics', label: 'Logistics' },
  { id: 'finance', label: 'Finance/Admin' },
  { id: 'intelligence', label: 'Intelligence and Investigations' },
];

const commandForms: RelatedForm[] = [
  { baseId: 'ICS_202', label: 'ICS 202 Objectives' },
  { baseId: 'ICS_203', label: 'ICS 203 Organization' },
];

export const SEAT_CATALOG: SeatDefinition[] = [
  {
    positionName: 'Incident Commander',
    rosterNames: ['Incident Commander', 'Unified Command'],
    slug: 'incident-commander',
    label: 'Incident Commander / Unified Command',
    section: 'command',
    activation: 'command-staff',
    panel: 'command',
    relatedForms: commandForms,
  },
  {
    positionName: 'Safety Officer',
    slug: 'safety-officer',
    label: 'Safety Officer',
    section: 'command',
    activation: 'command-staff',
    panel: 'safety',
    relatedForms: [
      { baseId: 'ICS_208', label: 'ICS 208 Safety Message' },
      { baseId: 'ICS_208HM', label: 'ICS 208HM' },
      { baseId: 'ICS_215A', label: 'ICS 215A Safety Analysis' },
    ],
  },
  {
    positionName: 'Liaison Officer',
    slug: 'liaison-officer',
    label: 'Liaison Officer',
    section: 'command',
    activation: 'command-staff',
    panel: 'liaison',
    relatedForms: [{ baseId: 'ICS_203', label: 'ICS 203 Organization' }],
  },
  {
    positionName: 'Public Information Officer',
    slug: 'public-information-officer',
    label: 'Public Information Officer',
    section: 'command',
    activation: 'command-staff',
    panel: 'pio',
    relatedForms: [{ baseId: 'ICS_202', label: 'ICS 202 Objectives' }],
  },
  {
    positionName: 'Legal Officer',
    slug: 'legal-officer',
    label: 'Legal Officer',
    section: 'command',
    activation: 'command-staff',
    panel: 'legal',
    relatedForms: [],
  },
  {
    positionName: 'Planning Section Chief',
    slug: 'planning-section-chief',
    label: 'Planning Section Chief',
    section: 'planning',
    activation: 'section-chief',
    panel: 'planning',
    relatedForms: [
      ...commandForms,
      { baseId: 'ICS_204', label: 'ICS 204 Assignment' },
      { baseId: 'ICS_209', label: 'ICS 209 Status' },
    ],
  },
  {
    positionName: 'Situation Unit Leader',
    slug: 'situation-unit-leader',
    label: 'Situation Unit',
    section: 'planning',
    activation: 'staffed',
    panel: 'situation',
    relatedForms: [{ baseId: 'ICS_209', label: 'ICS 209 Status' }],
  },
  {
    positionName: 'Resource Unit Leader',
    slug: 'resource-unit-leader',
    label: 'Resources Unit',
    section: 'planning',
    activation: 'staffed',
    panel: 'resources',
    relatedForms: [{ baseId: 'ICS_203', label: 'ICS 203 Organization' }],
  },
  {
    positionName: 'Documentation Unit Leader',
    slug: 'documentation-unit-leader',
    label: 'Documentation Unit',
    section: 'planning',
    activation: 'staffed',
    panel: 'documentation',
    relatedForms: [],
  },
  {
    positionName: 'Demobilization Unit Leader',
    slug: 'demobilization-unit-leader',
    label: 'Demobilization Unit',
    section: 'planning',
    activation: 'staffed',
    panel: 'demobilization',
    relatedForms: [],
  },
  {
    positionName: 'Operations Section Chief',
    slug: 'operations-section-chief',
    label: 'Operations Section Chief',
    section: 'operations',
    activation: 'section-chief',
    panel: 'operations',
    relatedForms: [
      { baseId: 'ICS_204', label: 'ICS 204 Assignment' },
      { baseId: 'ICS_215', label: 'ICS 215 Worksheet' },
      { baseId: 'ICS_215A', label: 'ICS 215A Safety Analysis' },
    ],
  },
  {
    positionName: 'Logistics Section Chief',
    slug: 'logistics-section-chief',
    label: 'Logistics Section Chief',
    section: 'logistics',
    activation: 'section-chief',
    panel: 'logistics',
    relatedForms: [
      { baseId: 'ICS_205', label: 'ICS 205 Communications' },
      { baseId: 'ICS_206', label: 'ICS 206 Medical' },
    ],
  },
  {
    positionName: 'Communications Unit Leader',
    slug: 'communications-unit-leader',
    label: 'Communications Unit',
    section: 'logistics',
    activation: 'staffed',
    panel: 'communications',
    relatedForms: [
      { baseId: 'ICS_205', label: 'ICS 205 Communications' },
      { baseId: 'ICS_205A', label: 'ICS 205A Contacts' },
    ],
  },
  {
    positionName: 'Medical Unit Leader',
    slug: 'medical-unit-leader',
    label: 'Medical Unit',
    section: 'logistics',
    activation: 'staffed',
    panel: 'medical',
    relatedForms: [{ baseId: 'ICS_206', label: 'ICS 206 Medical' }],
  },
  {
    positionName: 'Facilities Unit Leader',
    slug: 'facilities-unit-leader',
    label: 'Facilities Unit',
    section: 'logistics',
    activation: 'staffed',
    panel: 'facilities',
    relatedForms: [],
  },
  {
    positionName: 'Supply Unit Leader',
    slug: 'supply-unit-leader',
    label: 'Supply Unit',
    section: 'logistics',
    activation: 'staffed',
    panel: 'supply',
    relatedForms: [{ baseId: 'ICS_213RR', label: 'ICS 213RR Resource Request' }],
  },
  {
    positionName: 'Ground Support Unit Leader',
    slug: 'ground-support-unit-leader',
    label: 'Ground Support Unit',
    section: 'logistics',
    activation: 'staffed',
    panel: 'ground-support',
    relatedForms: [],
  },
  {
    positionName: 'Food Unit Leader',
    slug: 'food-unit-leader',
    label: 'Food Unit',
    section: 'logistics',
    activation: 'staffed',
    panel: 'sustainment',
    relatedForms: [],
  },
  {
    positionName: 'Finance Section Chief',
    slug: 'finance-section-chief',
    label: 'Finance/Administration Section Chief',
    section: 'finance',
    activation: 'section-chief',
    panel: 'finance',
    relatedForms: [],
  },
  {
    positionName: 'Time Unit Leader',
    slug: 'time-unit-leader',
    label: 'Time Unit',
    section: 'finance',
    activation: 'staffed',
    panel: 'time',
    relatedForms: [],
  },
  {
    positionName: 'Procurement Unit Leader',
    slug: 'procurement-unit-leader',
    label: 'Procurement Unit',
    section: 'finance',
    activation: 'staffed',
    panel: 'procurement',
    relatedForms: [{ baseId: 'ICS_213RR', label: 'ICS 213RR Resource Request' }],
  },
  {
    positionName: 'Cost Unit Leader',
    slug: 'cost-unit-leader',
    label: 'Cost Unit',
    section: 'finance',
    activation: 'staffed',
    panel: 'cost',
    relatedForms: [],
  },
  {
    positionName: 'Compensation Unit Leader',
    slug: 'compensation-unit-leader',
    label: 'Compensation/Claims',
    section: 'finance',
    activation: 'staffed',
    panel: 'claims',
    relatedForms: [],
  },
  {
    positionName: 'Intel/Investigations Section Chief',
    slug: 'intel-investigations',
    label: 'Intelligence and Investigations Section Chief',
    section: 'intelligence',
    activation: 'staffed',
    panel: 'intel',
    relatedForms: [],
  },
];

const SECTION_CHIEFS = new Set([
  'Operations Section Chief',
  'Planning Section Chief',
  'Logistics Section Chief',
  'Finance Section Chief',
]);

export function seatBySlug(slug: string): SeatDefinition | undefined {
  return SEAT_CATALOG.find((seat) => seat.slug === slug);
}

export function rosterNamesForSeat(seat: SeatDefinition): readonly string[] {
  return seat.rosterNames ?? [seat.positionName];
}

export function seatByPositionName(
  positionName: string
): SeatDefinition | undefined {
  return SEAT_CATALOG.find((seat) =>
    rosterNamesForSeat(seat).includes(positionName)
  );
}

export function isSeatActive(
  seat: SeatDefinition,
  options: { escalated: boolean; staffedPositionNames: ReadonlySet<string> }
): boolean {
  if (seat.activation === 'staffed') {
    return true;
  }
  if (!options.escalated) return false;
  if (seat.activation === 'section-chief') {
    return SECTION_CHIEFS.has(seat.positionName);
  }
  return seat.activation === 'command-staff';
}

export function activeSeats(options: {
  escalated: boolean;
  staffedPositionNames: ReadonlySet<string>;
}): SeatDefinition[] {
  return SEAT_CATALOG.filter((seat) => isSeatActive(seat, options));
}

export type SeatLanding =
  | { kind: 'dashboard' }
  | { kind: 'one'; slug: string }
  | { kind: 'many'; seats: SeatDefinition[] };

/** Where `/iap/:incidentId` opens for this user. */
export function landingForPositions(
  positionNames: readonly string[],
  options: { escalated: boolean; staffedPositionNames: ReadonlySet<string> }
): SeatLanding {
  const held = positionNames
    .map((name) => seatByPositionName(name))
    .filter((seat): seat is SeatDefinition => !!seat)
    .filter((seat) => isSeatActive(seat, options));
  const unique = [...new Map(held.map((seat) => [seat.slug, seat])).values()];
  if (unique.length === 1) return { kind: 'one', slug: unique[0].slug };
  if (unique.length > 1) return { kind: 'many', seats: unique };
  return { kind: 'dashboard' };
}

/** Planning P phase title to the seat that owns the work of that meeting. */
const PHASE_SEAT_SLUG: Record<string, string> = {
  'IC/UC Objectives': 'incident-commander',
  'Command & General Staff Meeting': 'incident-commander',
  'Prepare Tactics': 'operations-section-chief',
  'Tactics Meeting': 'operations-section-chief',
  'Prepare Planning': 'planning-section-chief',
  'Planning Meeting': 'planning-section-chief',
  'IAP Prep & Approval': 'planning-section-chief',
  'Operations Briefing': 'operations-section-chief',
};

export function seatSlugForPlanningPhase(phaseTitle: string): string | undefined {
  return PHASE_SEAT_SLUG[phaseTitle];
}
